/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 * 
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 * 
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains the hook to use an lookups by id.
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/*
 * Used to create a context.
 */
import * as React from 'react';

/*
 * Used to type the state of a request. 
 */
import { IRequestState, RequestState } from '@ngt/request-utilities';

/*
 * Used to type actions.
 */
import { ActionCreators } from 'immer-reducer';

/*
 * Used to get access to dispatch to dispatch actions to the store.
 */
import { useDispatch } from 'react-redux';

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/*
 * Used to get access to backend types.
 */
import * as Dtos from '../../api/dtos';

/*
 * used to type actions.
 */
import { LookupsReducer, lookupsActions, useLookupsSelector, lookupsSelectors, ILookupsStore } from '../../store/modules/utility/lookups';

/*
 * Used to remove the first parameter from the base action creator function type.
 */
import { OmitFirstArg, OmitFirstThreeArgs } from '../../utilities/OmitArgs';

/*
 * Used to type action creators with first parameter bound to a static. 
 */
import { BoundActionCreator } from '../../utilities/BoundActionCreator';

/*
 * Used to bind action creators to the store but keep their assigned type property.
 */
import bindActionCreatorsWithType from '../../utilities/bindActionCreatorsWithType';

/*
 * ---------------------------------------------------------------------------------
 * Interfaces
 * ---------------------------------------------------------------------------------
 */

export interface IUseLookupsByFormDefinitionIdActions {
    load: BoundActionCreator<OmitFirstThreeArgs<typeof lookupsActions.loadByIds>>;
    clear: BoundActionCreator<OmitFirstThreeArgs<typeof lookupsActions.clearByIds>>;
}

/*
 * ---------------------------------------------------------------------------------
 * Functions
 * ---------------------------------------------------------------------------------
 */

const useLookupsByFormDefinitionId = <LookupsType extends Dtos.ILookup>(formDefinitionId: number, autoLoad?: boolean): [
    LookupsType[] | null,
    IRequestState<Dtos.ResponseStatus>,
    IUseLookupsByFormDefinitionIdActions
] => {
    const dispatch = useDispatch();

    const actions: IUseLookupsByFormDefinitionIdActions = React.useMemo(() => {
        const load = () => lookupsActions.loadByIds(formDefinitionId);
        load.type = lookupsActions.loadByIds.type;

        const clear = () => lookupsActions.clearByIds(formDefinitionId);
        clear.type = lookupsActions.clearByIds.type;

        return bindActionCreatorsWithType({
            load,
            clear
        }, dispatch);
    }, [lookupsActions, formDefinitionId, dispatch]);

    const lookupsSelector = React.useCallback((state: ILookupsStore) => {
        return lookupsSelectors.lookupsByIds(state, formDefinitionId);
    }, [lookupsSelectors.lookupsByIds, formDefinitionId]);

    const loadStateSelector = React.useCallback((state: ILookupsStore) => {
        return lookupsSelectors.loadStateByIds(state, formDefinitionId);
    }, [lookupsSelectors.loadStateByIds, formDefinitionId]);

    const lookups = useLookupsSelector(lookupsSelector);

    const loadState = useLookupsSelector(loadStateSelector);

    React.useEffect(() => {
        if (autoLoad) {
            actions.load();

            return () => {
                actions.clear();
            };
        }

        return () => { };
    }, [autoLoad, formDefinitionId]);

    return [
        lookups as LookupsType[] | null,
        loadState,
        actions
    ];
};

/*
 * ---------------------------------------------------------------------------------
 * Default Export
 * ---------------------------------------------------------------------------------
 */

export default useLookupsByFormDefinitionId;